// The picker's screen (E32-S9-T3): the sticky budget bar, the banners, and the guides with their
// segments, from mockups/production-picker.html. It holds the draft selection; picker-page.tsx owns
// the data and the save. Every word is the catalogue's (src/i18n/en.ts).
import type { Principal } from "@waypoint/contracts";
import { Button, cn, focusRing } from "@waypoint/surface";
import { useState } from "react";
import { Link } from "wouter";
import { t } from "../../i18n/t.js";
import { useOrgName } from "../../theme/theme-gate.js";
import { packName } from "./issue-format.js";
import { StatusChip } from "../../ui/form.js";
import { Gauge } from "../../ui/gauge.js";
import {
  fits,
  mmss,
  refusalFor,
  sameSet,
  TIERS,
  usedOf,
  whenOf,
  type Entry,
  type Ledger,
  type PlanRead,
  type Refusal,
} from "./plan-model.js";

export interface PickerViewProps {
  readonly principal: Principal;
  readonly data: PlanRead;
  readonly entries: readonly Entry[];
  readonly ledger: Ledger;
  readonly stored: ReadonlySet<string>;
  readonly storedSeconds: number;
  readonly readOnly: boolean;
  readonly saving: boolean;
  /** A message from the page that outlives a reload ("Plan saved.", "Someone else changed this plan"). */
  readonly notice: string | undefined;
  readonly onEdit: () => void;
  /** Saves the selection; answers the reason it was not saved, or undefined when it was. */
  readonly onSave: (keys: ReadonlySet<string>) => Promise<string | undefined>;
}

function refusalWords(r: Refusal, ledger: Ledger): string {
  return r.kind === "over"
    ? t("plan.refuse.over", {
        id: r.entry.titleId,
        total: mmss(r.total),
        budget: mmss(ledger.budget),
        over: mmss(r.over),
      })
    : t("plan.refuse.room", {
        id: r.entry.titleId,
        seconds: mmss(r.entry.seconds),
        left: mmss(r.left),
        total: mmss(r.total),
        budget: mmss(ledger.budget),
      });
}

function groupsOf(entries: readonly Entry[]): { packId: string; list: Entry[] }[] {
  const groups = new Map<string, Entry[]>();
  for (const e of entries) groups.set(e.packId, [...(groups.get(e.packId) ?? []), e]);
  return [...groups].map(([packId, list]) => ({ packId, list }));
}

export function PickerView(props: PickerViewProps) {
  const { principal, data, entries, ledger, stored, storedSeconds, readOnly } = props;
  const org = useOrgName();
  const [selected, setSelected] = useState<ReadonlySet<string>>(stored);
  const [refuse, setRefuse] = useState<string | undefined>(undefined);
  const [problem, setProblem] = useState<string | undefined>(undefined);
  const groups = groupsOf(entries);
  const [open, setOpen] = useState<ReadonlySet<string>>(
    () =>
      new Set(
        groups
          .filter((g, i) => i === 0 || g.list.some((e) => !e.reference && stored.has(e.key)))
          .map((g) => g.packId),
      ),
  );
  const [openReference, setOpenReference] = useState<ReadonlySet<string>>(new Set());

  const budget = ledger.budget;
  const used = usedOf(ledger, selected);
  const over = used > budget;
  const storedOver = storedSeconds > budget;
  const changed = !sameSet(selected, stored);
  const added = [...selected].filter((k) => !stored.has(k));
  const removed = [...stored].filter((k) => !selected.has(k));
  const blockedBy = new Map(
    data.summary.segments.map((s) => [`${s.packId}/${s.titleId}`, s.blocked]),
  );
  const blockedPicked = entries.filter(
    (e) => selected.has(e.key) && (blockedBy.get(e.key)?.length ?? 0) > 0,
  );
  const idOf = (key: string) => ledger.byKey.get(key)?.titleId ?? key;
  const role = principal.roles.includes("content-owner")
    ? t("role.content-owner")
    : principal.roles.includes("tenant-admin")
      ? t("role.tenant-admin")
      : t("role.client-reviewer");

  const edited = (next: ReadonlySet<string>) => {
    setSelected(next);
    setRefuse(undefined);
    setProblem(undefined);
    props.onEdit();
  };

  const toggle = (entry: Entry) => {
    if (readOnly || entry.reference) return;
    if (selected.has(entry.key)) {
      edited(new Set([...selected].filter((k) => k !== entry.key)));
    } else if (fits(ledger, selected, entry)) {
      edited(new Set([...selected, entry.key]));
    } else {
      setProblem(undefined);
      setRefuse(refusalWords(refusalFor(ledger, selected, entry), ledger));
    }
  };

  const save = async () => {
    setProblem(await props.onSave(selected));
  };

  const message = refuse ?? problem ?? props.notice;
  const plan = data.plan;

  const row = (e: Entry) => {
    const on = selected.has(e.key);
    const addable = on || fits(ledger, selected, e);
    const blocked = blockedBy.get(e.key) ?? [];
    const held = on && stored.has(e.key) && e.kind === "estimated" && storedOver;
    const whyId = `why-${e.key}`;
    const room = Math.max(0, Math.max(budget, storedSeconds) - used);
    return (
      <li
        key={e.key}
        data-on={on}
        className={cn(
          "grid grid-cols-[auto_minmax(0,1fr)] items-start gap-x-3 gap-y-1 rounded-8 px-2 py-2 sm:grid-cols-[auto_minmax(0,1fr)_auto]",
          on ? "bg-wp-accent/13" : "hover:bg-br-sky/9",
        )}
      >
        <input
          type="checkbox"
          id={`cb-${e.key}`}
          checked={on}
          disabled={readOnly}
          aria-disabled={addable ? undefined : true}
          aria-describedby={addable ? undefined : whyId}
          onChange={() => {
            toggle(e);
          }}
          className={cn(
            "mt-0.75 size-4.5 cursor-pointer accent-wp-accent disabled:cursor-not-allowed",
            !addable && "cursor-not-allowed opacity-55",
            focusRing,
          )}
        />
        <label htmlFor={`cb-${e.key}`} className="grid min-w-0 cursor-pointer gap-0.5">
          <span>
            <span className="mr-2 font-mono text-12 font-semibold text-wp-text-2">{e.titleId}</span>{" "}
            <span className="text-14">{e.title}</span>
          </span>
        </label>
        <span className="col-start-2 flex gap-1.5 font-mono text-13 font-medium whitespace-nowrap sm:col-start-3 sm:grid sm:text-right">
          <span className={cn(e.kind === "measured" && "font-semibold text-wp-accent")}>
            {mmss(e.seconds)}
          </span>
          <small className="font-sans text-11 font-normal text-wp-text-3">
            {e.kind === "measured"
              ? t("plan.seg.measured", { planned: mmss(e.planned) })
              : t("plan.seg.est")}
          </small>
        </span>
        {blocked.length > 0 || !addable || held ? (
          <span className="col-start-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-12 text-wp-text-2 sm:col-span-2">
            {blocked.length > 0 ? (
              <>
                <StatusChip tone="blocks">{t("plan.seg.blocked")}</StatusChip>
                <span>
                  {t(blocked.length > 1 ? "plan.seg.questionMany" : "plan.seg.questionOne", {
                    ids: "",
                  })}
                  {blocked.map((id, i) => (
                    <span key={id}>
                      {i > 0 ? ", " : null}
                      <Link
                        href={`/issues/${e.packId}#${id}`}
                        className={cn("font-semibold underline", focusRing)}
                      >
                        {id}
                      </Link>
                    </span>
                  ))}
                </span>
              </>
            ) : null}
            {held ? (
              <StatusChip tone="stop">{t("plan.seg.held", { budget: mmss(budget) })}</StatusChip>
            ) : null}
            {addable ? null : (
              <>
                <StatusChip tone="open">
                  {t("plan.seg.wontFit", { needs: mmss(e.seconds), left: mmss(room) })}
                </StatusChip>
                <span className="sr-only" id={whyId}>
                  {refusalWords(refusalFor(ledger, selected, e), ledger)}
                </span>
              </>
            )}
          </span>
        ) : null}
      </li>
    );
  };

  const guide = (packId: string, list: Entry[]) => {
    const real = list.filter((e) => !e.reference);
    const refs = list.filter((e) => e.reference);
    const picked = real.filter((e) => selected.has(e.key));
    const pickedTime = usedOf(ledger, new Set(picked.map((e) => e.key)));
    return (
      <div key={packId} className="grid gap-3.5">
        {real.length > 0 ? (
          <details
            open={open.has(packId)}
            onToggle={(ev) => {
              const isOpen = ev.currentTarget.open;
              setOpen(
                (o) => new Set([...o].filter((p) => p !== packId).concat(isOpen ? [packId] : [])),
              );
            }}
            className="rounded-12 border border-wp-line bg-wp-panel"
          >
            <summary
              className={cn(
                "flex cursor-pointer flex-wrap items-center gap-x-3.5 gap-y-2.5 rounded-12 px-4 py-3.5",
                focusRing,
              )}
            >
              <h2 className="min-w-0 flex-[1_1_14ch] text-16 font-semibold">{packName(packId)}</h2>
              <span className="text-13 text-wp-text-2 tabular-nums">
                {picked.length > 0
                  ? t("plan.guide.pickedTime", {
                      picked: picked.length,
                      total: real.length,
                      time: mmss(pickedTime),
                    })
                  : t("plan.guide.picked", { picked: 0, total: real.length })}
              </span>
            </summary>
            {TIERS.map((tier) => {
              const inTier = real.filter((e) => e.tier === tier);
              if (inTier.length === 0) return null;
              return (
                <div key={tier} className="px-4 pb-2">
                  <h3 className="flex gap-2.5 border-t border-wp-line pt-2.5 pb-1.5 text-11 font-semibold tracking-[0.08em] text-wp-text-3 uppercase">
                    {tier} <span>{inTier.length}</span>
                  </h3>
                  <ul className="grid list-none">{inTier.map(row)}</ul>
                </div>
              );
            })}
          </details>
        ) : null}
        {refs.length > 0 ? (
          <details
            open={openReference.has(packId)}
            onToggle={(ev) => {
              const isOpen = ev.currentTarget.open;
              setOpenReference(
                (o) => new Set([...o].filter((p) => p !== packId).concat(isOpen ? [packId] : [])),
              );
            }}
            className="rounded-12 border border-wp-line bg-wp-panel"
          >
            <summary
              className={cn(
                "flex cursor-pointer flex-wrap items-center gap-x-3.5 gap-y-2.5 rounded-12 px-4 py-3.5",
                focusRing,
              )}
            >
              <h2 className="min-w-0 flex-[1_1_14ch] text-16 font-semibold">
                {t("plan.guide.reference", { guide: packName(packId) })}
              </h2>
              <StatusChip tone="quiet">{t("plan.guide.referenceOnly")}</StatusChip>
            </summary>
            <p className="m-0 px-4 pb-3.5 text-13 text-wp-text-2">
              {t("plan.guide.referenceNote")}
            </p>
            <div className="px-4 pb-2">
              <ul className="grid list-none">
                {refs.map((e) => (
                  <li
                    key={e.key}
                    data-ref
                    className="grid grid-cols-[auto_minmax(0,1fr)] items-start gap-x-3 gap-y-1 rounded-8 px-2 py-2 opacity-62 sm:grid-cols-[auto_minmax(0,1fr)_auto]"
                  >
                    <input
                      type="checkbox"
                      id={`cb-${e.key}`}
                      disabled
                      checked={false}
                      readOnly
                      className="mt-0.75 size-4.5 cursor-not-allowed accent-wp-accent"
                    />
                    <label htmlFor={`cb-${e.key}`} className="min-w-0 cursor-not-allowed">
                      <span className="mr-2 font-mono text-12 font-semibold text-wp-text-2">
                        {e.titleId}
                      </span>{" "}
                      <span className="text-14">{e.title}</span>
                    </label>
                    <span className="col-start-2 text-13 text-wp-text-2 sm:col-start-3">
                      {t("plan.guide.notProduced")}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </details>
        ) : null}
      </div>
    );
  };

  return (
    <section aria-labelledby="page-title" className="grid gap-3.5">
      <a
        href="#picker"
        className={cn(
          "sr-only focus:not-sr-only focus:rounded-8 focus:bg-wp-panel focus:px-3 focus:py-2",
          focusRing,
        )}
      >
        {t("plan.skip")}
      </a>
      <div
        role="group"
        aria-label={t("plan.budget")}
        data-over={over}
        className="sticky top-0 z-10 -mx-4 grid gap-2 border-b border-wp-line bg-wp-panel px-4 pt-2.5 pb-3 shadow-2 sm:-mx-6 sm:px-6"
      >
        <div className="flex flex-wrap items-baseline gap-x-4.5 gap-y-1.5">
          <div className="text-22 leading-none font-bold tabular-nums sm:text-28">
            <span id="used">{mmss(used)}</span>{" "}
            <small className="text-16 font-medium text-wp-text-2">
              {t("plan.usedOf", { budget: mmss(budget) })}
            </small>
          </div>
          <div
            className={cn(
              "text-16 font-semibold tabular-nums",
              over ? "text-stop-soft" : "text-wp-accent",
            )}
          >
            {over
              ? t("plan.overBy", { time: mmss(used - budget) })
              : t("plan.remaining", { time: mmss(budget - used) })}
          </div>
          {readOnly ? null : (
            <div className="flex w-full items-center gap-2 sm:ml-auto sm:w-auto">
              <Button
                disabled={!changed || props.saving}
                onClick={() => {
                  setSelected(stored);
                  setRefuse(undefined);
                  setProblem(undefined);
                  props.onEdit();
                }}
              >
                {t("plan.discard")}
              </Button>
              <Button
                variant="primary"
                disabled={!changed || props.saving}
                onClick={() => {
                  void save();
                }}
              >
                {props.saving ? t("plan.saving") : t("plan.save")}
              </Button>
            </div>
          )}
        </div>
        <Gauge
          label={t("plan.meter")}
          value={used}
          max={budget}
          over={over}
          draft={changed}
          valueText={
            over
              ? t("plan.meterOver", {
                  used: mmss(used),
                  budget: mmss(budget),
                  over: mmss(used - budget),
                })
              : t("plan.meterUnder", {
                  used: mmss(used),
                  budget: mmss(budget),
                  left: mmss(budget - used),
                })
          }
          ticks={["0:00", mmss(budget / 2), mmss(budget)]}
        />
        <div className="flex min-h-5 flex-wrap items-center gap-x-3.5 gap-y-1.5 text-12 text-wp-text-2 sm:text-13">
          <span>
            {plan === null
              ? t("plan.pickedNone")
              : t("plan.pickedBy", { who: plan.pickedBy, when: whenOf(plan.pickedAt) })}
          </span>
          {changed ? (
            <span>
              {t("plan.draft", {
                added: added.map(idOf).join(", ") || "0",
                removed: removed.map(idOf).join(", ") || "0",
              })}
            </span>
          ) : null}
          <span role="status" aria-live="polite" className="font-semibold text-caution">
            {message}
          </span>
        </div>
      </div>

      <div id="picker" tabIndex={-1} className="grid gap-3.5 pb-16 outline-none">
        <div className="grid gap-1.5 pt-2">
          <p className="text-11 font-semibold tracking-[0.08em] text-wp-text-3 uppercase">
            {t("plan.eyebrow")}
          </p>
          <h1 id="page-title" className="text-22 leading-tight font-bold sm:text-28">
            {org === undefined ? t("plan.titleNoOrg") : t("plan.title", { org })}
          </h1>
          <p className="text-13 text-wp-text-3">
            {org === undefined
              ? t("plan.whoNoOrg", { role: role.toLowerCase() })
              : t("plan.who", { role: role.toLowerCase(), org })}
          </p>
          <p className="max-w-3xl text-14 text-wp-text-2">
            {t("plan.intro", { budget: mmss(budget) })}
          </p>
          {readOnly ? <p className="text-13 text-wp-text-2">{t("plan.readOnly")}</p> : null}
        </div>

        {storedOver ? (
          <div
            role="region"
            aria-label={t("plan.over.region")}
            className="grid gap-1 rounded-12 border border-stop/60 bg-stop/12 px-3.5 py-3"
          >
            <b className="text-15">
              {t("plan.over.title", { over: mmss(storedSeconds - budget) })}
            </b>
            <ul className="m-0 list-disc pl-4.5 text-13 text-wp-text-2">
              <li>{t("plan.over.hold", { budget: mmss(budget) })}</li>
              <li>{t("plan.over.kept")}</li>
              <li>{t("plan.over.allowed", { budget: mmss(budget) })}</li>
            </ul>
          </div>
        ) : blockedPicked.length > 0 ? (
          <div
            role="region"
            aria-label={t("plan.blocked.region")}
            className="grid gap-1 rounded-12 border border-caution/48 bg-caution/12 px-3.5 py-3"
          >
            <b className="text-15">
              {t(blockedPicked.length > 1 ? "plan.blocked.many" : "plan.blocked.one", {
                count: blockedPicked.length,
                ids: blockedPicked.map((e) => e.titleId).join(", "),
              })}
            </b>
            <ul className="m-0 list-disc pl-4.5 text-13 text-wp-text-2">
              <li>{t("plan.blocked.line", { budget: mmss(budget) })}</li>
            </ul>
          </div>
        ) : null}

        <div className="flex flex-wrap gap-x-4.5 gap-y-1.5 text-13 text-wp-text-2">
          <span>
            <b>{t("plan.seg.est")}</b> {t("plan.legend.est")}
          </span>
          <span>
            <b>{t("plan.seg.measuredWord")}</b> {t("plan.legend.measured")}
          </span>
          <Link href="/issues" className={cn("font-semibold underline", focusRing)}>
            {t("plan.legend.queue")}
          </Link>
        </div>

        {groups.map((g) => guide(g.packId, g.list))}
      </div>
    </section>
  );
}
